import 'intl';
import 'intl/locale-data/jsonp/en';
import 'intl/locale-data/jsonp/fr';
import isNumber from 'lodash/isNumber';
// @formatjs : Import order is important, cf. https://formatjs.io/docs/polyfills/
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/fr';

import { getI18nLocale, translate } from '@front/i18n';

import { Salutation } from '../types/commons';

import { getCurrentCurrency } from './country';
import { formatFloatNumber } from './numbers';

const t = translate('prices');

export const formatMinutes = (duration: number) => t('durations.minutes', { minutes: duration || 0 });

export const formatDuration = (duration: number = 0) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  return hours > 0
    ? t('durations.hours_minutes', {
        hours,
        minutes: minutes === 0 ? '' : String(minutes).padStart(2, '0'),
      })
    : t('durations.minutes', { minutes });
};

export type NumberFormatOptions = {
  precision?: number;
};

type IntlOptions = Intl.NumberFormatOptions;

const formatCurrency = (amount: number = 0, options: NumberFormatOptions = {}) => {
  const { precision } = options;

  const intlOptions: IntlOptions = {};
  if (isNumber(precision)) {
    intlOptions.minimumFractionDigits = precision;
    intlOptions.maximumFractionDigits = precision;
  }

  return new Intl.NumberFormat(getI18nLocale(), {
    style: 'currency',
    currency: getCurrentCurrency(),
    ...intlOptions,
  }).format(amount / 100);
};

export const formatPrice = (price?: number, options?: NumberFormatOptions) =>
  Number.isFinite(price) ? formatCurrency(price, options) : '';

export const formatPriceWithDuration = (price: number, duration?: number | null) =>
  duration
    ? t('.price_duration', {
        price: formatPrice(price),
        duration: formatDuration(duration),
      })
    : formatPrice(price);

export const formatPricePerHour = (price?: number, options?: NumberFormatOptions) =>
  Number.isFinite(price) ? t('.per_hour', { price: formatCurrency(price, options) }) : '';

export const formatPricePerSession = (price?: number) =>
  Number.isFinite(price) ? t('.per_session', { price: formatCurrency(price) }) : '';

export const formatPricePerWeek = (price?: number) =>
  Number.isFinite(price) ? t('.per_week', { price: formatCurrency(price) }) : '';

export const formatPricePerMonth = (price?: number) =>
  Number.isFinite(price) ? t('.per_month', { price: formatCurrency(price) }) : '';

type SalutationVariant = 'default' | 'short' | 'thisCustomer' | 'theCustomer';

type FormatSalutationOptions = {
  variant?: SalutationVariant;
};

const SALUTATION_KEYS: Record<Salutation, Record<SalutationVariant, string>> = {
  'M.': {
    default: 'man',
    short: 'man_short',
    theCustomer: 'man_customer',
    thisCustomer: 'man_this_customer',
  },
  Mme: {
    default: 'woman',
    short: 'woman_short',
    theCustomer: 'woman_customer',
    thisCustomer: 'woman_this_customer',
  },
};

export const formatSalutation = (salutation: Salutation, { variant = 'default' }: FormatSalutationOptions = {}) =>
  t(`salutations.${SALUTATION_KEYS[salutation][variant]}`);

export type Separator = 'and' | 'or' | 'plus';

const SEPARATOR_SYMBOLS: Partial<Record<Separator, string>> = {
  plus: '+',
};

export const formatStringListWithSeparator = (
  stringsList?: string[],
  { separator = 'and' }: { separator?: Separator } = {},
) => {
  if (!stringsList || stringsList.length === 0) return '';
  if (stringsList.length === 1) return stringsList[0];

  const firstPartOfString = stringsList.slice(0, -1).join(', ');
  const lastString = stringsList[stringsList.length - 1];

  return `${firstPartOfString} ${SEPARATOR_SYMBOLS[separator] || t(`base.${separator}`)} ${lastString}`;
};

export const formatRating = (rate: number | null, maxRate?: number) => {
  const formattedRate = typeof rate === 'number' ? formatFloatNumber(Math.round(rate * 10) / 10) : '0';

  return maxRate ? `${formattedRate}/${maxRate}` : formattedRate;
};

export const formatOrdinal = (numberToFormat: number): string => {
  const pr = new Intl.PluralRules(getI18nLocale(), { type: 'ordinal' });
  return `${numberToFormat}${t(`base.ordinal_suffixes.${pr.select(numberToFormat)}`)}`;
};

export const formatPercentage = (percentage: number, options: NumberFormatOptions = {}): string => {
  const { precision } = options;

  const isPercentageWithDecimal = percentage % 1 !== 0;

  if (isPercentageWithDecimal) {
    const numbersBeforeDecimal = percentage.toString().split('.')[0];
    const numbersAfterDecimal = percentage.toString().split('.')[1];
    const truncatedNumbersAfterDecimal = numbersAfterDecimal.slice(0, precision);

    return precision
      ? t('base.percentage.with_decimal', {
          percentage: numbersBeforeDecimal,
          numbersAfterDecimal: truncatedNumbersAfterDecimal,
        })
      : t('base.percentage.without_decimal', { percentage: numbersBeforeDecimal });
  }

  return t('base.percentage.without_decimal', { percentage });
};
